<template>
  <main
    class="app-shell"
    :class="{ 'has-song-background': currentBackgroundImage }"
    :style="appBackgroundStyle"
    @click="closeSearch"
  >
    <section class="player-frame" aria-label="Local music player">
      <ArtworkPanel
        :current-item="currentItem"
        :current-index="music.index"
        :current-time="music.currentTime"
        :lyrics="currentLyrics"
        :total="music.items.length"
        :metadata="currentMetadata"
      />

      <section class="deck-panel">
        <header class="deck-topbar" @click.stop>
          <div class="brand-mark" aria-label="Local player">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <button
            class="icon-button menu-button"
            type="button"
            @click="openSearch"
          >
            <i class="fa fa-search" aria-hidden="true"></i>
            <span class="sr-only">Search tracks</span>
          </button>
        </header>

        <SearchPanel
          ref="searchPanel"
          :show="showSearch"
          :music="music"
          :current-item="currentItem"
          :play-queue-set="playQueueSet"
          @close="closeSearch"
          @search="searchTracks"
          @set-current="setCurrent"
          @push-queue="pushQueue"
          @pop-queue="popQueue"
        />

        <div class="now-playing" style="display: none">
          <p class="kicker">
            LOCAL FILES
            <span v-if="music.items.length"
              >/ {{ music.items.length }} TRACKS</span
            >
          </p>
          <h1 :title="currentTitle">{{ currentTitle }}</h1>
          <p class="subtitle">{{ currentSubtitle }}</p>
        </div>

        <TrackList
          ref="trackList"
          :items="music.items"
          :current-item="currentItem"
          :cursor-index="music.cursorIndex"
          :play-queue-set="playQueueSet"
          @set-current="setCurrent"
          @push-queue="pushQueue"
          @pop-queue="popQueue"
        />

        <ProgressBar
          :current-time="music.currentTime"
          :duration="music.duration"
          :percent="music.progressPercent"
          :track-title="currentTitle"
          @seek="seekTo"
        />

        <PlayerControls
          :disabled="disabled"
          :paused="music.paused"
          :random="music.random"
          :loop="music.loop"
          :volume="music.volume"
          @add="addItems"
          @stop="stop"
          @previous="previous"
          @play-toggle="playOrPause"
          @next="next"
          @random="toggleRandom"
          @loop="toggleLoop"
          @volume="toggleVolume"
          @clear="removeItems"
        />
      </section>
    </section>

    <div v-if="music.changingProgress" class="progress-toast">
      {{ music.progress }}
    </div>
  </main>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  shallowReactive,
  nextTick,
} from "vue";
import { debounce } from "throttle-debounce";
import Music from "./Music.js";
import ArtworkPanel from "./components/ArtworkPanel.vue";
import PlayerControls from "./components/PlayerControls.vue";
import ProgressBar from "./components/ProgressBar.vue";
import SearchPanel from "./components/SearchPanel.vue";
import TrackList from "./components/TrackList.vue";
import {
  fetchArtistArtworkImages,
  fetchTrackLyrics,
  fetchTrackMetadata,
  parseTrackFileName,
} from "./musicMetadata.js";

const showSearch = ref(false);
const searchPanel = ref(null);
const trackList = ref(null);
const currentMetadata = ref(null);
const playlistMetadataByName = ref({});
const artistBackgroundImagesBySinger = ref({});
const currentBackgroundImageIndex = ref(0);
const currentFetchedLyrics = ref("");
let metadataController = null;
let playlistMetadataController = null;
let artistArtworkController = null;
let lyricsController = null;
let backgroundRotationTimer = null;
let backgroundPreloadToken = 0;
const BACKGROUND_ROTATION_MS = 6000;
const MIN_SINGER_BACKGROUND_IMAGES = 50;
const music = shallowReactive(new Music());
music.init();

const currentItem = computed(() => music.items[music.index]);
const disabled = computed(() => music.items.length === 0);
const playQueueSet = computed(() => new Set(music.playQueue));
const currentTrackInfo = computed(() =>
  parseTrackFileName(currentItem.value?.name)
);
const currentTitle = computed(() =>
  currentItem.value ? currentTrackInfo.value.title : "No Track Selected"
);
const currentLyrics = computed(() => currentFetchedLyrics.value);
const currentSongImage = computed(() => currentMetadata.value?.songImage || "");
const currentSinger = computed(() =>
  normalizeSinger(currentTrackInfo.value.artist)
);
const currentArtistId = computed(
  () =>
    currentMetadata.value?.artistId ||
    playlistMetadataByName.value[currentItem.value?.name]?.artistId ||
    ""
);
const currentArtistArtworkKey = computed(
  () => `${currentSinger.value}|${currentArtistId.value || "name"}`
);
const sameSingerBackgroundImages = computed(() => {
  if (!currentSinger.value) {
    return [];
  }

  const seenImages = new Set();
  const playlistImages = music.items.reduce((images, item) => {
    const itemSinger = normalizeSinger(parseTrackFileName(item.name).artist);
    const songImage = playlistMetadataByName.value[item.name]?.songImage;

    if (itemSinger === currentSinger.value) {
      addUniqueImage(images, seenImages, songImage);
    }

    return images;
  }, []);

  const fetchedArtistImages =
    artistBackgroundImagesBySinger.value[currentArtistArtworkKey.value] ||
    artistBackgroundImagesBySinger.value[`${currentSinger.value}|name`] ||
    [];

  return fetchedArtistImages.reduce((images, songImage) => {
    addUniqueImage(images, seenImages, songImage);
    return images;
  }, playlistImages);
});
const currentBackgroundImage = computed(
  () =>
    sameSingerBackgroundImages.value[currentBackgroundImageIndex.value] ||
    currentSongImage.value ||
    ""
);
const appBackgroundStyle = computed(() =>
  currentBackgroundImage.value
    ? {
        "--song-background-image": `url("${escapeCssUrl(
          currentBackgroundImage.value
        )}")`,
      }
    : {}
);
const currentSubtitle = computed(() =>
  currentItem.value
    ? currentTrackInfo.value.artist ||
      `Track ${music.index + 1} in local library`
    : "Add audio files to begin"
);

const openSearch = async () => {
  showSearch.value = true;
  await nextTick();
  searchPanel.value?.focusInput();
};

const closeSearch = () => {
  showSearch.value = false;
  searchPanel.value?.blurInput();
};

const isSearchFocused = () => searchPanel.value?.isInputFocused();
const addItems = () => music.addItems();
const stop = () => music.stop();
const previous = () => music.pre();
const playOrPause = () => music.playOrPause();
const next = () => music.next();
const toggleRandom = () => music.toggleRandom();
const toggleLoop = () => music.toggleLoop();
const toggleVolume = () => music.toggleVolume();
const removeItems = () => music.removeItems();
const setCurrent = (index) => music.setCurrent(index);
const pushQueue = (index) => music.pushPlayQueue(index);
const popQueue = (index) => music.popPlayQueue(index);
const searchTracks = (query) => music.search(query);
const seekTo = (ratio) => music.seekTo(ratio);

onMounted(() => {
  window.onkeydown = handleKeyboard;
  document.addEventListener("visibilitychange", handleVisibilityChange);

  navigator.mediaSession?.setActionHandler("nexttrack", () => music.next());
  navigator.mediaSession?.setActionHandler("previoustrack", () => music.pre());
  navigator.mediaSession?.setActionHandler("pause", () => music.pause());
  navigator.mediaSession?.setActionHandler("play", () => music.play());
});

onBeforeUnmount(() => {
  window.onkeydown = null;
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  metadataController?.abort();
  playlistMetadataController?.abort();
  artistArtworkController?.abort();
  lyricsController?.abort();
  stopBackgroundRotation();
});

function handleVisibilityChange() {
  if (document.hidden) {
    stopBackgroundRotation();
    backgroundPreloadToken += 1;
    return;
  }

  startBackgroundRotation();
}

function handleKeyboard(e) {
  let index = -1;
  switch (e.code) {
    case "Space":
      if (showSearch.value) return;
      e.preventDefault();
      music.playOrPause();
      break;
    case "Enter":
      if (showSearch.value) {
        const item = music.searchItems[music.searchCursorIndex];
        if (item) {
          music.setCurrent(item.index);
          closeSearch();
        }
      } else {
        music.playCurrentCursor();
      }
      break;
    case "KeyS":
      if (showSearch.value) return;
      music.stop();
      break;
    case "KeyI":
      if (showSearch.value) return;
      music.toggleLoop();
      break;
    case "KeyR":
      if (showSearch.value) return;
      music.toggleRandom();
      break;
    case "KeyV":
      if (showSearch.value) return;
      music.toggleVolume();
      break;
    case "KeyH":
    case "ArrowLeft":
      if (showSearch.value) return;
      if (e.shiftKey) {
        music.backward();
      } else {
        setTimeout(() => music.pre());
      }
      break;
    case "KeyL":
    case "ArrowRight":
      if (showSearch.value) return;
      if (e.shiftKey) {
        music.forward();
      } else {
        setTimeout(() => music.next());
      }
      break;
    case "KeyK":
      if (showSearch.value) return;
      e.preventDefault();
      music.cursorUp();
      break;
    case "ArrowUp":
      e.preventDefault();
      setTimeout(() => {
        if (showSearch.value) {
          music.searchCursorUp();
          if (isSearchFocused()) searchPanel.value?.blurInput();
        } else {
          music.cursorUp();
        }
      });
      break;
    case "KeyJ":
      if (showSearch.value) return;
      e.preventDefault();
      music.cursorDown();
      break;
    case "ArrowDown":
      e.preventDefault();
      setTimeout(() => {
        if (showSearch.value) {
          music.searchCursorDown();
          if (isSearchFocused()) searchPanel.value?.blurInput();
        } else {
          music.cursorDown();
        }
      });
      break;
    case "Slash":
      if (showSearch.value) {
        setTimeout(() => {
          if (!isSearchFocused()) searchPanel.value?.focusInput();
        }, 0);
        return;
      }
      openSearch();
      break;
    case "Backspace":
      if (showSearch.value && !isSearchFocused()) {
        searchPanel.value?.focusInput();
      }
      break;
    case "Escape":
      setTimeout(() => closeSearch());
      break;
    case "KeyA":
      if (showSearch.value) {
        const item = music.searchItems[music.searchCursorIndex];
        if (!isSearchFocused() && item) index = item.index;
      } else {
        index = music.cursorIndex;
      }

      if (index >= 0) {
        if (playQueueSet.value.has(index)) {
          music.popPlayQueue(index);
        } else {
          music.pushPlayQueue(index);
        }
      }
      break;
    default:
      break;
  }
}

watch(
  () => music.cursorIndex,
  debounce(200, false, (val) => trackList.value?.scrollToIndex(val))
);

watch(
  () => music.searchCursorIndex,
  debounce(160, false, (val) => searchPanel.value?.scrollToIndex(val))
);

watch(
  () => currentItem.value?.name,
  async (name) => {
    metadataController?.abort();
    currentMetadata.value = null;

    if (!name) {
      return;
    }

    metadataController = new AbortController();
    try {
      currentMetadata.value = await fetchTrackMetadata(
        name,
        metadataController.signal
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        currentMetadata.value = null;
      }
    }
  },
  { immediate: true }
);

watch(
  () => music.items.map((item) => item.name).join("\n"),
  async (itemsKey) => {
    playlistMetadataController?.abort();

    if (!itemsKey) {
      playlistMetadataByName.value = {};
      return;
    }

    const playlistItems = [...music.items];
    const controller = new AbortController();
    playlistMetadataController = controller;

    const metadataEntries = await Promise.all(
      playlistItems.map(async (item) => {
        try {
          const metadata = await fetchTrackMetadata(
            item.name,
            controller.signal
          );
          return [item.name, metadata];
        } catch (error) {
          if (error.name === "AbortError") {
            return null;
          }

          const trackInfo = parseTrackFileName(item.name);
          return [
            item.name,
            {
              title: trackInfo.title,
              artist: trackInfo.artist,
              artistId: "",
              songImage: "",
              artistImage: "",
              source: "",
            },
          ];
        }
      })
    );

    if (controller.signal.aborted) {
      return;
    }

    playlistMetadataByName.value = Object.fromEntries(
      metadataEntries.filter(Boolean)
    );
  },
  { immediate: true }
);

watch(
  () => currentArtistArtworkKey.value,
  async (artistKey) => {
    artistArtworkController?.abort();

    const [singer, artistId] = artistKey.split("|");
    if (!singer) {
      return;
    }

    const existingImages = artistBackgroundImagesBySinger.value[singer];
    if (existingImages?.length >= MIN_SINGER_BACKGROUND_IMAGES) {
      return;
    }

    const controller = new AbortController();
    artistArtworkController = controller;

    try {
      const images = await fetchArtistArtworkImages(
        currentTrackInfo.value.artist,
        controller.signal,
        MIN_SINGER_BACKGROUND_IMAGES,
        artistId
      );

      if (controller.signal.aborted) {
        return;
      }

      artistBackgroundImagesBySinger.value = {
        ...artistBackgroundImagesBySinger.value,
        [artistKey]: images,
      };
    } catch (error) {
      if (error.name !== "AbortError") {
        artistBackgroundImagesBySinger.value = {
          ...artistBackgroundImagesBySinger.value,
          [artistKey]: [],
        };
      }
    }
  },
  { immediate: true }
);

watch(
  () =>
    `${currentSinger.value}\n${sameSingerBackgroundImages.value.join("\n")}`,
  () => {
    startBackgroundRotation({ resetIndex: true });
  },
  { immediate: true }
);

watch(
  () => music.paused,
  (paused) => {
    if (paused) {
      stopBackgroundRotation();
      backgroundPreloadToken += 1;
      return;
    }

    startBackgroundRotation();
  },
  { immediate: true }
);

watch(
  () => currentItem.value?.name,
  async (name) => {
    lyricsController?.abort();
    currentFetchedLyrics.value = "";

    if (!name) {
      return;
    }

    lyricsController = new AbortController();
    try {
      currentFetchedLyrics.value = await fetchTrackLyrics(
        name,
        lyricsController.signal
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        currentFetchedLyrics.value = "";
      }
    }
  },
  { immediate: true }
);

function normalizeSinger(artist = "") {
  return artist.trim().replace(/\s+/g, " ").toLowerCase();
}

function escapeCssUrl(url = "") {
  return url.replace(/"/g, '\\"');
}

function addUniqueImage(images, seenImages, songImage) {
  if (songImage && !seenImages.has(songImage)) {
    seenImages.add(songImage);
    images.push(songImage);
  }
}

function startBackgroundRotation({ resetIndex = false } = {}) {
  stopBackgroundRotation();
  backgroundPreloadToken += 1;

  if (resetIndex) {
    currentBackgroundImageIndex.value = 0;
  }

  if (
    document.hidden ||
    music.paused ||
    sameSingerBackgroundImages.value.length <= 1
  ) {
    return;
  }

  backgroundRotationTimer = window.setInterval(() => {
    const imageCount = sameSingerBackgroundImages.value.length;
    if (imageCount <= 1) {
      stopBackgroundRotation();
      currentBackgroundImageIndex.value = 0;
      return;
    }

    rotateToNextReadyBackgroundImage(
      (currentBackgroundImageIndex.value + 1) % imageCount
    );
  }, BACKGROUND_ROTATION_MS);
}

function stopBackgroundRotation() {
  if (backgroundRotationTimer) {
    window.clearInterval(backgroundRotationTimer);
    backgroundRotationTimer = null;
  }
}

async function rotateToNextReadyBackgroundImage(nextIndex) {
  const imageUrl = sameSingerBackgroundImages.value[nextIndex];
  if (!imageUrl) {
    return;
  }

  const preloadToken = backgroundPreloadToken;
  await preloadImage(imageUrl);

  if (preloadToken !== backgroundPreloadToken) {
    return;
  }

  currentBackgroundImageIndex.value = nextIndex;
}

function preloadImage(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = async () => {
      if (typeof image.decode !== "function") {
        resolve();
        return;
      }

      try {
        await image.decode();
      } catch (error) {
        // Use the already-loaded image even if decode() is unavailable or fails.
      }
      resolve();
    };
    image.onerror = resolve;
    image.src = url;
  });
}
</script>

<style lang="less">
@import "./styles/var.less";

.app-shell {
  position: relative;
  z-index: 0;
  display: flex;
  overflow: hidden;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  color: #f7f2ee;
  background: radial-gradient(
      circle at 9% 82%,
      rgba(39, 98, 181, 0.58),
      transparent 34%
    ),
    radial-gradient(circle at 88% 12%, rgba(164, 30, 43, 0.32), transparent 28%),
    linear-gradient(135deg, #121516 0%, #070808 54%, #15100f 100%);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
  isolation: isolate;
}

.app-shell.has-song-background::before,
.app-shell.has-song-background::after {
  position: absolute;
  inset: 0;
  pointer-events: none;
  content: "";
}

.app-shell.has-song-background::before {
  z-index: 0;
  background-image: var(--song-background-image);
  background-position: 0 0;
}

.app-shell.has-song-background::after {
  z-index: 0;
  background: radial-gradient(
      circle at 50% 36%,
      rgba(255, 255, 255, 0.14),
      transparent 34%
    ),
    linear-gradient(135deg, rgba(6, 7, 8, 0.14), rgba(6, 6, 6, 0.38)),
    linear-gradient(180deg, rgba(12, 13, 14, 0.18), rgba(0, 0, 0, 0.36));
}

.player-frame {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(360px, 1fr);
  width: min(100%, 1040px);
  min-height: 820px;
  margin: auto;
  overflow: hidden;
  border-radius: 28px;
  background: rgba(9, 10, 11, 0.65);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.54);
  backdrop-filter: blur(4px);
}

.deck-panel {
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 34px 40px 30px;
  background-size: 64px 64px, 64px 64px, auto;
}

.deck-topbar {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.brand-mark {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 16px;
}

.brand-mark span {
  width: 2px;
  background: rgba(255, 255, 255, 0.72);
}

.brand-mark span:nth-child(1) {
  height: 5px;
}

.brand-mark span:nth-child(2) {
  height: 8px;
}

.brand-mark span:nth-child(3) {
  height: 12px;
}

.brand-mark span:nth-child(4) {
  height: 9px;
}

.brand-mark span:nth-child(5) {
  height: 6px;
}

.icon-button {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #f7f2ee;
  background: transparent;
  cursor: pointer;
}

.icon-button:hover,
.icon-button:focus-visible {
  border-color: rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.08);
  outline: none;
}

.now-playing {
  position: relative;
  z-index: 1;
  margin: 46px 0 28px;
}

.kicker {
  margin-bottom: 14px;
  color: #a39b98;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
}

.kicker span {
  color: #b3393d;
}

.now-playing h1 {
  max-width: 100%;
  min-height: 1.16em;
  margin: 0;
  overflow: hidden;
  color: #f2f0ef;
  font-size: 44px;
  font-weight: 500;
  line-height: 1.16;
  letter-spacing: 0;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.subtitle {
  margin-top: 14px;
  color: #8f8987;
  font-size: 12px;
}

.progress-toast {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 10;
  min-width: 180px;
  padding: 18px 22px;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  color: #f7f2ee;
  background: rgba(10, 10, 10, 0.88);
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  backdrop-filter: blur(16px);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 860px) {
  .app-shell {
    padding: 18px;
  }

  .player-frame {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .deck-panel {
    padding: 26px 22px 24px;
  }

  .now-playing {
    margin-top: 34px;
  }

  .now-playing h1 {
    max-width: 100%;
    font-size: 32px;
  }
}
</style>
